import { isResource, resolveId } from '@medplum/core'
import type {
  CodeableConcept,
  Coding,
  Condition,
  ConditionEvidence,
  Encounter,
  Group,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import {
  codeableConcept,
  codeFromCodeableConcept,
  displayFromCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  CONDITION_CATEGORY,
  CONDITION_CLINICIAL_STATUS,
  CONDITION_VERIFICATION_STATUS,
  ELATION_CONDITION_CLINICAL_STATUS,
  ELATION_CONDITION_RANK_EXTENSION,
  ICD10_SYSTEM,
} from '#src/codeSystems.ts'
import { type DateString, toDateString } from '#src/dates.ts'
import { Extension } from '#src/decorators.ts'
import { ExtendedResource, type ExportFields } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import {
  CONDITION_CATEGORIES,
  CONDITION_CLINICAL_STATUSES,
  CONDITION_VERIFICATION_STATUSES,
  ConditionCategory,
  ConditionClinicalStatus,
  ConditionVerificationStatus,
} from '#src/models/conditions/constants.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export type ExtendedConditionParams = Pick<
  Condition,
  'subject' | 'recorder'
> & {
  title?: string
  category?: ConditionCategory
  clinicalStatus?: ConditionClinicalStatus
  verificationStatus?: ConditionVerificationStatus
  recordedDate?: Date
}

export interface ExtendedConditionSearchQueryOptions {
  subject: Reference<Patient> | undefined
  includeDeleted?: boolean
  activeOnly?: boolean
}

export class ExtendedCondition extends ExtendedResource<Condition> {
  static readonly RESOURCE_TYPE = 'Condition'

  @Extension({
    url: ELATION_CONDITION_RANK_EXTENSION,
    kind: 'valueInteger',
  })
  private _elationRank?: number

  static isInstance(value: unknown): value is Condition | ExtendedCondition {
    return isResource(value) && isOfType(value, 'Condition')
  }

  static isCurrentClinicalStatus(
    status: ConditionClinicalStatus | undefined
  ): boolean {
    return (
      status === ConditionClinicalStatus.ACTIVE ||
      status === ConditionClinicalStatus.CONTROLLED ||
      status === ConditionClinicalStatus.RECURRENCE ||
      status === ConditionClinicalStatus.RELAPSE ||
      status === ConditionClinicalStatus.UNKNOWN
    )
  }

  constructor({
    subject,
    recorder,
    category = ConditionCategory.DIAGNOSIS,
    clinicalStatus = ConditionClinicalStatus.ACTIVE,
    verificationStatus = ConditionVerificationStatus.CONFIRMED,
    recordedDate,
  }: ExtendedConditionParams) {
    super({ subject, recorder })
    this.clinicalStatus = clinicalStatus
    this.verificationStatus = verificationStatus
    this.category = category
    this.recordedDate = recordedDate ?? new Date()
  }

  get title(): string {
    return this.resource.text?.div ?? 'New Problem'
  }

  set title(title: string) {
    const _title = title?.trim()
    this.resource.text = _title
      ? {
          status: 'additional',
          div: _title,
        }
      : undefined
  }

  get searchKeywords(): string[] {
    // Match on both the coded label (`code.text`) and the narrative
    // (`text.div`, via `title`). These can diverge in either direction — a
    // stale `code.text` ("Diabetes") hiding an accurate narrative
    // ("Onychomycosis…"), or vice versa — so a condition must be findable by
    // either wording, not just whichever `Diagnosis.name` happens to prefer.
    return [
      this.code?.text ?? '',
      this.title,
      displayFromCodeableConcept(this.code) ?? '',
      this.icd10Code?.code ?? '',
    ]
  }

  get code(): CodeableConcept | undefined {
    return this.resource.code
  }

  set code(code: CodeableConcept | undefined) {
    this.resource.code = code
  }

  get icd10Code(): Coding | undefined {
    return this.code?.coding?.find((c) => c.system === ICD10_SYSTEM)
  }

  get isCurrent(): boolean {
    return ExtendedCondition.isCurrentClinicalStatus(this.clinicalStatus)
  }

  get note(): string {
    return this.resource.note?.[0]?.text ?? ''
  }

  set note(note: string) {
    const _note = note?.trim()
    this.resource.note = _note ? [{ text: _note }] : undefined
  }

  // For display, prefer `onsetDateString`. `new Date('YYYY-MM-DD')` parses as
  // UTC midnight per spec, so formatting in US tz shifts the day back by one.
  get onsetDate(): Date | undefined {
    return this.resource.onsetDateTime
      ? new Date(this.resource.onsetDateTime)
      : undefined
  }

  set onsetDate(onsetDate: Date | string | null | undefined) {
    if (!onsetDate) {
      this.resource.onsetDateTime = undefined
      return
    }
    this.resource.onsetDateTime =
      onsetDate instanceof Date ? onsetDate.toISOString() : onsetDate
  }

  get onsetDateString(): DateString | null {
    return this.resource.onsetDateTime
      ? toDateString(this.resource.onsetDateTime)
      : null
  }

  get onsetYear(): number | undefined {
    return this.onsetDate ? this.onsetDate.getFullYear() : undefined
  }

  get elationRank(): number | undefined {
    return this._elationRank
  }

  set elationRank(value: number | undefined) {
    this._elationRank = value
  }

  get recordedDate(): Date | undefined {
    return this.resource.recordedDate
      ? new Date(this.resource.recordedDate)
      : undefined
  }

  set recordedDate(recordedDate: Date | undefined) {
    this.resource.recordedDate = recordedDate?.toISOString()
  }

  get clinicalStatus(): ConditionClinicalStatus {
    return (
      (codeFromCodeableConcept(
        this.resource.clinicalStatus,
        CONDITION_CLINICIAL_STATUS
      ) as ConditionClinicalStatus) ??
      (codeFromCodeableConcept(
        this.resource.clinicalStatus,
        ELATION_CONDITION_CLINICAL_STATUS
      ) as ConditionClinicalStatus) ??
      ConditionClinicalStatus.UNKNOWN
    )
  }

  set clinicalStatus(clinicalStatus: ConditionClinicalStatus) {
    this.resource.clinicalStatus = codeableConcept(
      CONDITION_CLINICAL_STATUSES[clinicalStatus]
    )
  }

  get verificationStatus(): ConditionVerificationStatus {
    return (
      (codeFromCodeableConcept(
        this.resource.verificationStatus,
        CONDITION_VERIFICATION_STATUS
      ) as ConditionVerificationStatus) ??
      ConditionVerificationStatus.UNCONFIRMED
    )
  }

  set verificationStatus(verificationStatus: ConditionVerificationStatus) {
    this.resource.verificationStatus = codeableConcept(
      CONDITION_VERIFICATION_STATUSES[verificationStatus]
    )
    if (verificationStatus === ConditionVerificationStatus.ENTERED_IN_ERROR) {
      // https://arc.net/l/quote/kagnfyjj
      // Constraint con-5 not met: Condition.clinicalStatus SHALL NOT be present if verification Status is entered-in-error
      delete this.resource.clinicalStatus
    }
  }

  get recorder(): Reference<Practitioner> | undefined {
    const recorder = this.resource.recorder
    if (!recorder) {
      return undefined
    }
    invariant(
      isOfType(recorder, 'Practitioner'),
      'Recorder must be a practitioner'
    )
    return recorder
  }

  set recorder(recorder: Reference<Practitioner> | undefined) {
    this.resource.recorder = recorder
  }

  get evidence(): ConditionEvidence[] {
    return this.resource.evidence ?? []
  }

  set evidence(evidence: ConditionEvidence[] | undefined) {
    this.resource.evidence = evidence
  }

  getEvidenceWithDetail(): ConditionEvidence[] {
    return this.resource.evidence && this.resource.evidence.length > 0
      ? this.resource.evidence
      : [
          {
            detail: [],
          },
        ]
  }

  getEvidenceDetailAt(index: number): ConditionEvidence['detail'] | undefined {
    return this.resource.evidence?.[index]?.detail ?? undefined
  }

  setEvidenceDetailAt(
    index: number,
    detail: ConditionEvidence['detail']
  ): void {
    if (this.resource.evidence?.[index]) {
      this.resource.evidence[index].detail = detail
    }
  }

  get encounter(): Reference<Encounter> | undefined {
    return this.resource.encounter
  }

  set encounter(value: Reference<Encounter> | undefined) {
    this.resource.encounter = value
  }

  get subject(): Reference<Group | Patient> | undefined {
    return this.resource.subject
  }

  get participantSubject(): Reference<Patient> | undefined {
    if (isOfType(this.resource.subject, 'Patient')) {
      return this.resource.subject
    }
    console.warn(
      { subject: this.resource.subject, conditionId: this.id },
      'Subject is not a Patient'
    )
    return undefined
  }

  get evidenceSummary(): { code: string[]; detail: string[] }[] {
    return this.evidence.map((e) => ({
      code: e.code?.map((c) => codeFromCodeableConcept(c) ?? '') ?? [],
      detail: e.detail?.map((d) => resolveId(d) ?? '') ?? [],
    }))
  }

  get category(): ConditionCategory {
    return codeFromCodeableConcept(
      this.resource.category,
      CONDITION_CATEGORY
    ) as ConditionCategory
  }

  get categoryDisplay(): string {
    return CONDITION_CATEGORIES[this.category].display as string
  }

  set category(category: ConditionCategory) {
    this.resource.category = [
      ...(this.resource.category ?? []).filter(
        (c) => !c.coding?.find((coding) => coding.system === CONDITION_CATEGORY)
      ),
      codeableConcept(CONDITION_CATEGORIES[category]),
    ]
  }

  get isDeleted(): boolean {
    return (
      this.verificationStatus === ConditionVerificationStatus.ENTERED_IN_ERROR
    )
  }

  markDeleted(): void {
    this.verificationStatus = ConditionVerificationStatus.ENTERED_IN_ERROR
  }

  static queryBase: QueryTypes = {
    'verification-status:not': ConditionVerificationStatus.ENTERED_IN_ERROR,
    _sort: ['onset-date', '-_lastUpdated'],
    _count: 1000,
  }

  static searchQuery({
    subject,
    includeDeleted = false,
    activeOnly = false,
  }: ExtendedConditionSearchQueryOptions): QueryTypes {
    const query: QueryTypes = {
      subject,
      _sort: ['onset-date', '-_lastUpdated'],
      _count: 1000,
    }
    if (!includeDeleted) {
      query['verification-status:not'] =
        ConditionVerificationStatus.ENTERED_IN_ERROR
    }
    if (activeOnly) {
      query['clinical-status'] = ConditionClinicalStatus.ACTIVE
    }
    return query
  }

  protected getExportFields(): ExportFields {
    return {
      id: this.id,
      title: this.title,
      code: codeFromCodeableConcept(this.code),
      note: this.note,
      onsetDate: this.onsetDate,
      onsetYear: this.onsetYear,
      clinicalStatus: this.clinicalStatus,
      verificationStatus: this.verificationStatus,
      recorderId: resolveId(this.recorder),
      patientId: resolveId(this.subject),
      evidence: this.evidenceSummary,
      recordedDate: this.recordedDate,
      category: this.category,
      categoryDisplay: this.categoryDisplay,
      elationRank: this.elationRank,
    }
  }
}
